import { NewStudentData } from "@/api/student";
import { Classroom } from "@/entities/classroom";
import { StudentGrade } from "@/entities/student";
import { generateNewStudentId, generateStudentLoginId } from "@/utils/student";
import parse from "csv-parse/lib/sync";
import fileSaver from "file-saver";
import encoding from "encoding-japanese";
import { generateRandomPassword, validPassword } from "./password";
import { CustomProperty } from "@/entities/custom_property";
import { validEmail } from "./email";
import { School } from "@/entities/school";

function addEventListenersToReader(
  reader: FileReader,
  resolve: (
    value:
      | string
      | ArrayBuffer
      | PromiseLike<string | ArrayBuffer | null>
      | null
  ) => void,
  reject: (reason?: any) => void
) {
  reader.onload = () => {
    resolve(reader.result);
  };
  reader.onerror = () => {
    reject(reader.error);
  };
}

export async function readAsText(
  blob: Blob,
  { onlyUtf8 }: { onlyUtf8: boolean } = { onlyUtf8: false }
): Promise<string | ArrayBuffer | null> {
  const detected = encoding.detect(new Uint8Array(await blob.arrayBuffer()));
  //SJISかUTF-8のみ使う前提
  const _encoding = detected === "SJIS" ? "Shift_JIS" : "UTF-8";

  if (onlyUtf8 && detected !== "UTF8") {
    throw new Error("File must be UTF-8 encoded");
  }

  if (!detected) {
    console.warn("Encoding not detected. Falling back to UTF-8.");
  }
  if (detected && detected !== "SJIS" && detected !== "UTF8") {
    console.warn("Unsupported encoding detected. Text may be garbled.");
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    addEventListenersToReader(reader, resolve, reject);
    reader.readAsText(blob, _encoding);
  });
}

type StudentRow = {
  name: string;
  classroomName: string;
  grade: StudentGrade;
  id: string;
  password: string;
  loginId: string;
  googleAccount: string;
  microsoftAccount: string;
  customProperties: { title: string; value: string | number }[];
};

const studentGrades: StudentGrade[] = [
  "小1",
  "小2",
  "小3",
  "小4",
  "小5",
  "小6",
  "中1",
  "中2",
  "中3",
  "高1",
  "高2",
  "高3",
  "その他"
];

function convertRecordsToStudentRows(
  records: {
    [key: string]: string | StudentGrade | object[];
  }[]
): StudentRow[] {
  return records.map(record => {
    const customProperties = [];
    if (record.customProperty && record.customPropertyValue !== undefined) {
      customProperties.push({
        title: record.customProperty as string,
        value: record.customPropertyValue as string | number
      });
    }

    let i = 2; // 2から始める（既に最初の customProperty は処理されているため）
    while (
      record[`customProperty${i}`] &&
      record[`customPropertyValue${i}`] !== undefined
    ) {
      customProperties.push({
        title: record[`customProperty${i}`] as string,
        value: record[`customPropertyValue${i}`] as string | number
      });
      i++;
    }

    return {
      name: record.name as string,
      classroomName: record.classroomName as string,
      grade: record.grade as StudentGrade,
      id: "",
      password: record.password as string,
      loginId: (record.loginId ?? "") as string,
      googleAccount: (record.googleAccount ?? "") as string,
      microsoftAccount: (record.microsoftAccount ?? "") as string,
      customProperties: customProperties
    };
  });
}

function validateStudentRow(
  row: StudentRow,
  classroomNames: string[],
  customPropsTitles: string[]
) {
  if (row.name.length === 0) {
    throw new Error(
      "生徒名が入力されていない行が存在します。生徒名は入力必須です。"
    );
  }

  if (!classroomNames.includes(row.classroomName)) {
    throw new Error(
      "存在しない教室名が入力されています。データを修正して再度試してみてください。"
    );
  }

  if (!studentGrades.includes(row.grade)) {
    throw new Error(
      "存在しない学年が入力されています。データを修正して再度試してみてください。"
    );
  }

  // ログインIDが入力されなかった場合は、バックエンドでデフォルトのスクールIDとstudentIDを連結させる
  if (String(row.loginId).length > 0) {
    if (!/^[\x21-\x7E]+$/.test(row.loginId)) {
      throw new Error("ログインIDは半角英数字で設定してください。");
    }
    if (String(row.loginId).length < 5) {
      throw new Error("ログインIDは半角英数字5文字以上で設定してください。");
    }
  }

  if (row.password.length > 0 && !validPassword(row.password)) {
    throw new Error(
      "パスワードの形式が間違っている行が存在します。パスワードは8〜64文字で、英字と、数字または記号を含めてください（スペースと全角文字は使えません）。"
    );
  }

  if (row.googleAccount.length > 0 && !validEmail(row.googleAccount)) {
    throw new Error("Googleアカウントの形式が間違っている行が存在します。");
  }

  if (row.microsoftAccount.length > 0 && !validEmail(row.microsoftAccount)) {
    throw new Error("Microsoftアカウントの形式が間違っている行が存在します。");
  }

  if (row.customProperties) {
    const titles = new Set();
    for (const cp of row.customProperties) {
      if (!customPropsTitles.includes(cp.title)) {
        throw new Error(
          `以下のオリジナル情報が登録されていません: ${cp.title}`
        );
      }

      if (titles.has(cp.title)) {
        throw new Error(
          `同一のオリジナル情報を複数登録しようとしています。登録するオリジナル情報はかぶらないようにしてください: ${cp.title}`
        );
      }
      titles.add(cp.title);
    }
  }
}

function convertToNewStudentDataFromRow(
  row: StudentRow,
  classrooms: Classroom[],
  customProperties: CustomProperty[]
): NewStudentData {
  const matchClassrooms = classrooms.filter(
    c => c.data.name === row.classroomName
  );
  const matchCustomProperties = customProperties.filter(cp =>
    row.customProperties.some(rowCp => rowCp.title === cp.data.title)
  );

  if (matchClassrooms.length === 0) {
    throw new Error("correct classroom is not found.");
  }
  if (row.customProperties.length > 0 && matchCustomProperties.length === 0) {
    throw new Error("correct custom property is not found.");
  }

  const properties = row.customProperties
    ? row.customProperties.map(cp => {
        const title = cp.title;
        const matchedElement = customProperties.find(
          element => element.data.title === title
        );
        if (!matchedElement) {
          throw new Error(`No matched element found for title: ${title}`);
        }
        const cpRefId = matchedElement.ref.id;

        return { id: cpRefId, value: cp.value };
      })
    : [];

  return {
    classroomRef: matchClassrooms[0].ref,
    id: row.id,
    name: row.name,
    grade: row.grade,
    classSchedules: [],
    emailReceivers: [],
    password: row.password,
    properties: properties,
    loginId: row.loginId,
    gmail: row.googleAccount ?? "",
    microsoftEmail: row.microsoftAccount ?? "",
    // TODO: ↓CSVから取ったデータに含まれないので、空文字を渡すことで対応。今後、CSVからも登録できるようにする？
    characteristic: ""
  };
}

export async function validateCsvAndConvertToNewStudentDatas(
  file: File,
  classrooms: Classroom[],
  customProperties: CustomProperty[],
  idContext: {
    school: School;
    existingIds: string[];
    existingLoginIds: string[];
  }
): Promise<NewStudentData[]> {
  const { existingIds, existingLoginIds, school } = idContext;

  const ids = [...existingIds];
  const loginIds = [...existingLoginIds];
  const text = await readAsText(file);

  if (typeof text !== "string") {
    throw new Error(
      "ファイルを正常に読み込むことができませんでした。CSVファイルのデータが正しいかどうか確認してください"
    );
  }

  const records = parse(text, {
    columns: true,
    skipEmptyLines: true
  });

  if (records.constructor !== Array) {
    throw new Error(
      "ファイルを正常に読み込むことができませんでした。CSVファイルのデータが正しいかどうか確認してください"
    );
  }

  const rows: StudentRow[] = convertRecordsToStudentRows(records);
  // validate rows (exist classroomName in classrooms / all id is unique / grade in studentGrades)
  for (const i in rows) {
    const index = Number(i);

    validateStudentRow(
      rows[index],
      classrooms.map(c => c.data.name),
      customProperties.map(c => c.data.title)
    );
    if (rows[index].password.length === 0) {
      rows[index].password = generateRandomPassword();
    }
    // idを自動で生成
    const studentId = generateNewStudentId(ids);
    rows[index].id = studentId;
    ids.push(studentId);

    //ログインIDが空のものは自動で生成
    if (rows[index].loginId) {
      loginIds.push(rows[index].loginId);
    } else {
      const generatedId = generateStudentLoginId(loginIds, {
        schoolId: school.data.id,
        studentId
      });
      rows[index].loginId = generatedId;
      loginIds.push(generatedId);
    }
  }

  return rows.map(row =>
    convertToNewStudentDataFromRow(row, classrooms, customProperties)
  );
}

/**
 * CSV用のエスケープ関数
 * カンマ、ダブルクォート、改行を含む値を適切に処理する
 */
export function escapeCsvField(field: string): string {
  if (field.includes('"') || field.includes(",") || field.includes("\n")) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

export function exportToUTF8Csv(text: string, fileName: string) {
  const unicodeList = [];
  for (let i = 0; i < text.length; i += 1) {
    unicodeList.push(text.charCodeAt(i));
  }

  const Utf8CodeList = encoding.convert(unicodeList, "UTF8", "UNICODE");
  const bom = [0xef, 0xbb, 0xbf];
  const uInt8List = new Uint8Array([...bom, ...Utf8CodeList]);
  const writeData = new Blob([uInt8List], { type: "text/csv" });

  fileSaver.saveAs(writeData, `${fileName}.csv`);
}
